import { AlertTriangle, Check, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ToastTone = 'success' | 'danger' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  text: string;
}

interface ToastContextValue {
  notify: (text: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_ICON = { success: Check, danger: AlertTriangle, info: Info } as const;
const DISMISS_AFTER = 5000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const { t } = useTranslation();

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (text: string, tone: ToastTone = 'success') => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, tone, text }]);
      window.setTimeout(() => dismiss(id), DISMISS_AFTER);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        `polite` rather than `assertive`: these confirm something a person just
        did. Interrupting a screen reader mid-sentence to say "Saved." is worse
        than waiting for a pause.

        Positioned with logical inset properties so it lands on the correct side
        in both directions without a second rule.
      */}
      <div
        className="pointer-events-none fixed bottom-4 z-toast flex flex-col gap-2"
        style={{ insetInlineEnd: 'var(--space-4)' }}
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => {
          const Icon = TONE_ICON[toast.tone];
          return (
            <div
              key={toast.id}
              className="dio-toast pointer-events-auto"
              data-tone={toast.tone}
            >
              <Icon
                className="dio-icon flex-none"
                aria-hidden="true"
                style={{
                  color:
                    toast.tone === 'danger'
                      ? 'var(--color-danger-fg)'
                      : toast.tone === 'success'
                        ? 'var(--color-success-fg)'
                        : 'var(--color-info-fg)',
                }}
              />
              <span className="flex-1">{toast.text}</span>
              <button
                type="button"
                className="dio-btn dio-btn--ghost dio-btn--icon dio-btn--sm"
                onClick={() => dismiss(toast.id)}
                aria-label={t('common.close')}
              >
                <X className="dio-icon" aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast used outside ToastProvider');
  return value;
}
